import type {
  BlurType,
  ColorAdjustments,
  ColorCorrectRequest,
  ColorGradeRequest,
  ColorizeRequest,
  ConsistencySheetRequest,
  DeblurRequest,
  DenoiseRequest,
  EnhanceRequest,
  EnhancementStrength,
  ExpandDirection,
  FaceEnhanceRequest,
  FaceSwapRequest,
  GenerateRequest,
  ImageLabImageFormat,
  ImageToImageRequest,
  InpaintRequest,
  MultiAngleRequest,
  OutpaintRequest,
  RemoveBackgroundRequest,
  RemoveObjectRequest,
  SketchToImageRequest,
  SourceImageRef,
  StyleTransferRequest,
  TryOnRequest,
  UpscaleRequest,
  VariationsRequest,
  WhiteBalancePreset,
  ZoomRequest,
} from '../../../domain';
import { stripDataUrlPrefix } from '../canvas/maskMath';
import type { ImageRect } from '../canvas/selectionMath';
import { rectToZoomParams } from '../canvas/selectionMath';
import type { Size } from '../canvas/canvasMath';
import type { StudioParams } from '../../shared/panels/swatchSelection';
import { mergePromptWithFragments } from '../../shared/panels/swatchSelection';

/**
 * True when at least one of the three `SourceImageRef` variants is set.
 * Exported so panels can dedupe the inline `!!source && (id || url || base64)`
 * boilerplate against the same shape the request builders use.
 *
 * Accepts `undefined` as a convenience for panel callsites where `source`
 * is `SourceImageRef | undefined` from props — saves a `!!source &&` at
 * every callsite.
 */
export const hasSourceImage = (source: SourceImageRef | undefined): boolean =>
  !!source && (!!source.sourceImageId || !!source.sourceImageUrl || !!source.sourceImageBase64);

/**
 * Translate a SourceImageRef into a namespaced reference triplet (e.g.,
 * `styleReferenceImageId` for StyleTransfer, `targetImageId` for FaceSwap,
 * `referenceImageId` for ColorGrade). Returns an object with only the
 * defined field set; spread into the request DTO.
 */
function refToNamespacedFields<Prefix extends string>(
  ref: SourceImageRef,
  prefix: Prefix,
): Record<`${Prefix}Id` | `${Prefix}Url` | `${Prefix}Base64`, string | undefined> {
  return {
    [`${prefix}Id`]: ref.sourceImageId,
    [`${prefix}Url`]: ref.sourceImageUrl,
    [`${prefix}Base64`]: ref.sourceImageBase64,
  } as Record<`${Prefix}Id` | `${Prefix}Url` | `${Prefix}Base64`, string | undefined>;
}

/**
 * Per-panel form state. Each panel's local state shape is intentionally tiny
 * — just the fields the user can edit — so the corresponding `buildXxxRequest`
 * is a pure projection from "form" to the typed request DTO.
 */

export interface GenerateFormState {
  prompt: string;
  negativePrompt: string;
  width: number;
  height: number;
  numImages: number;
  seed: string; // free text so we can distinguish "empty" from "0"
  guidanceScale: number;
  model: string;
}

export interface InpaintFormState {
  prompt: string;
  strength: number;
  model: string;
}

export interface RemoveBackgroundFormState {
  outputFormat: ImageLabImageFormat;
  refineMask: boolean;
  model: string;
}

export interface ImageToImageFormState {
  prompt: string;
  strength: number;
  preserveComposition: boolean;
  model: string;
}

export interface UpscaleFormState {
  scaleFactor: number;
  enhanceDetails: boolean;
  denoiseStrength: number;
  model: string;
}

/**
 * Enhance picks individual refinements out of a curated set. The BE accepts
 * any string in the array — keep the FE list short and the BE side picks
 * sensible defaults if it doesn't recognize a name.
 */
export const ENHANCE_OPTIONS = ['Lighting', 'Sharpness', 'Color', 'Detail'] as const;
export type EnhanceOption = (typeof ENHANCE_OPTIONS)[number];

export interface EnhanceFormState {
  strength: EnhancementStrength;
  enhancements: EnhanceOption[];
  model: string;
}

export interface VariationsFormState {
  numVariations: number;
  variationStrength: number;
  preserveStyle: boolean;
  model: string;
}

export interface RemoveObjectFormState {
  model: string;
}

export interface OutpaintFormState {
  prompt: string;
  top: number;
  right: number;
  bottom: number;
  left: number;
  model: string;
}

export interface DenoiseFormState {
  strength: number;
  preserveDetails: boolean;
  faceAware: boolean;
  model: string;
}

export interface DeblurFormState {
  blurType: BlurType;
  strength: number;
  preserveDetails: boolean;
  model: string;
}

export interface FaceEnhanceFormState {
  enhancementLevel: EnhancementStrength;
  preserveIdentity: boolean;
  model: string;
}

export interface ColorCorrectFormState {
  whiteBalance: WhiteBalancePreset;
  exposure: number;
  contrast: number;
  saturation: number;
  temperature: number;
  tint: number;
  highlights: number;
  shadows: number;
  vibrance: number;
  model: string;
}

export interface ColorizeFormState {
  colorStyle: string;
  enhanceFaces: boolean;
  saturation: number;
  model: string;
}

export interface MultiAngleFormState {
  numAngles: number;
  angleStepDegrees: number;
  preserveIdentity: boolean;
  model: string;
}

export interface StyleTransferFormState {
  styleStrength: number;
  preserveContent: boolean;
  model: string;
}

export interface FaceSwapFormState {
  sourceFaceIndex: number;
  targetFaceIndex: number;
  enhanceFace: boolean;
  model: string;
}

export interface ColorGradeFormState {
  gradingStyle: string;
  strength: number;
  model: string;
}

export interface TryOnFormState {
  garmentCategory: string;
  additionalPrompt: string;
  model: string;
}

export interface SketchToImageFormState {
  prompt: string;
  sketchStrength: number;
  width: number;
  height: number;
  model: string;
}

export interface ZoomFormState {
  detailPrompt: string;
  zoomFactor: number;
  model: string;
}

/**
 * ConsistencySheet variation kinds — the cell-axis along which the sheet
 * varies the subject. Curated FE list; BE accepts any string.
 */
export const CONSISTENCY_VARIATION_KINDS = [
  'pose',
  'expression',
  'outfit-detail',
  'context',
  'lighting',
] as const;
export type ConsistencyVariationKind = (typeof CONSISTENCY_VARIATION_KINDS)[number];

export const CONSISTENCY_CELL_OPTIONS = [4, 6, 9] as const;
export type ConsistencyCellCount = (typeof CONSISTENCY_CELL_OPTIONS)[number];

export interface ConsistencySheetFormState {
  sheetCellCount: ConsistencyCellCount;
  variationKind: ConsistencyVariationKind;
  additionalPrompt: string;
  model: string;
}

export const DEFAULT_GENERATE_STATE: GenerateFormState = {
  prompt: '',
  negativePrompt: '',
  width: 1024,
  height: 1024,
  numImages: 1,
  seed: '',
  guidanceScale: 7.5,
  model: '',
};

export const DEFAULT_INPAINT_STATE: InpaintFormState = {
  prompt: '',
  strength: 0.85,
  model: '',
};

export const DEFAULT_REMOVE_BACKGROUND_STATE: RemoveBackgroundFormState = {
  outputFormat: 'Png',
  refineMask: true,
  model: '',
};

export const DEFAULT_IMAGE_TO_IMAGE_STATE: ImageToImageFormState = {
  prompt: '',
  strength: 0.75,
  preserveComposition: true,
  model: '',
};

export const DEFAULT_UPSCALE_STATE: UpscaleFormState = {
  scaleFactor: 2,
  enhanceDetails: true,
  denoiseStrength: 0.3,
  model: '',
};

export const DEFAULT_ENHANCE_STATE: EnhanceFormState = {
  strength: 'Medium',
  enhancements: ['Lighting', 'Sharpness', 'Color', 'Detail'],
  model: '',
};

export const DEFAULT_VARIATIONS_STATE: VariationsFormState = {
  numVariations: 3,
  variationStrength: 0.6,
  preserveStyle: true,
  model: '',
};

export const DEFAULT_REMOVE_OBJECT_STATE: RemoveObjectFormState = {
  model: '',
};

export const DEFAULT_OUTPAINT_STATE: OutpaintFormState = {
  prompt: '',
  top: 0,
  right: 256,
  bottom: 0,
  left: 256,
  model: '',
};

export const DEFAULT_DENOISE_STATE: DenoiseFormState = {
  strength: 0.5,
  preserveDetails: true,
  faceAware: true,
  model: '',
};

export const DEFAULT_DEBLUR_STATE: DeblurFormState = {
  blurType: 'Auto',
  strength: 0.65,
  preserveDetails: true,
  model: '',
};

export const DEFAULT_FACE_ENHANCE_STATE: FaceEnhanceFormState = {
  enhancementLevel: 'Medium',
  preserveIdentity: true,
  model: '',
};

export const DEFAULT_COLOR_CORRECT_STATE: ColorCorrectFormState = {
  whiteBalance: 'Auto',
  exposure: 0,
  contrast: 0,
  saturation: 0,
  temperature: 0,
  tint: 0,
  highlights: 0,
  shadows: 0,
  vibrance: 0,
  model: '',
};

export const DEFAULT_COLORIZE_STATE: ColorizeFormState = {
  colorStyle: '',
  enhanceFaces: true,
  saturation: 0.6,
  model: '',
};

export const DEFAULT_MULTI_ANGLE_STATE: MultiAngleFormState = {
  numAngles: 3,
  angleStepDegrees: 30,
  preserveIdentity: true,
  model: '',
};

export const DEFAULT_STYLE_TRANSFER_STATE: StyleTransferFormState = {
  styleStrength: 0.7,
  preserveContent: true,
  model: '',
};

export const DEFAULT_FACE_SWAP_STATE: FaceSwapFormState = {
  sourceFaceIndex: 0,
  targetFaceIndex: 0,
  enhanceFace: true,
  model: '',
};

export const DEFAULT_COLOR_GRADE_STATE: ColorGradeFormState = {
  gradingStyle: '',
  strength: 0.65,
  model: '',
};

/**
 * TryOn garment categories — kept matching B15's free-text contract.
 * The list is curated for the FE picker; the BE accepts any string.
 */
export const TRY_ON_GARMENT_CATEGORIES = [
  'top',
  'bottom',
  'dress',
  'outerwear',
  'footwear',
  'accessory',
] as const;
export type TryOnGarmentCategory = (typeof TRY_ON_GARMENT_CATEGORIES)[number];

export const DEFAULT_TRY_ON_STATE: TryOnFormState = {
  garmentCategory: 'top',
  additionalPrompt: '',
  model: '',
};

export const DEFAULT_SKETCH_TO_IMAGE_STATE: SketchToImageFormState = {
  prompt: '',
  sketchStrength: 0.75,
  width: 1024,
  height: 1024,
  model: '',
};

export const DEFAULT_ZOOM_STATE: ZoomFormState = {
  detailPrompt: '',
  zoomFactor: 2,
  model: '',
};

export const DEFAULT_CONSISTENCY_SHEET_STATE: ConsistencySheetFormState = {
  sheetCellCount: 4,
  variationKind: 'pose',
  additionalPrompt: '',
  model: '',
};

const trimToUndefined = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed.length === 0 ? undefined : trimmed;
};

const parseSeed = (raw: string): number | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? Math.trunc(n) : undefined;
};

/**
 * Build a `GenerateRequest` from form state. Throws when the prompt is
 * empty (after Studio-fragment merge) so the calling component can branch
 * on a typed error rather than ad-hoc `if (!state.prompt) return` early-
 * returns in every panel.
 *
 * Studio params (Bucket 2): when `studioParams` carries `promptFragments`
 * they're appended to the user prompt as comma-separated tokens; when it
 * carries an `aspectRatio` the panel's w/h fields are overridden. This
 * lets a "Photoreal + 16:9 + Moody" swatch combo deterministically reshape
 * the request without the user having to edit the panel.
 *
 * Allows an empty user prompt as long as Studio fragments contribute
 * something — picking just "Photoreal" with no prompt becomes a
 * "photorealistic, high detail, natural lighting" request.
 */
export function buildGenerateRequest(
  state: GenerateFormState,
  sessionId: string | undefined,
  studioParams?: StudioParams,
): GenerateRequest {
  const fragments = studioParams?.promptFragments ?? [];
  const prompt = mergePromptWithFragments(state.prompt, fragments);
  if (!prompt) {
    throw new Error('Prompt is required to generate.');
  }
  const width = studioParams?.aspectRatio?.width ?? state.width;
  const height = studioParams?.aspectRatio?.height ?? state.height;
  return {
    sessionId,
    model: trimToUndefined(state.model),
    prompt,
    negativePrompt: trimToUndefined(state.negativePrompt),
    width,
    height,
    numImages: state.numImages,
    seed: parseSeed(state.seed),
    guidanceScale: state.guidanceScale,
  };
}

/**
 * Build an `InpaintRequest`. Requires a mask (canvas-drawn, supplied as a
 * data URL by `KonvaCanvas.onMaskChange`) and a source image. Strips the
 * `data:image/png;base64,` prefix because the SSGP v3 `MaskBase64` field
 * receives the bare payload and the backend re-prefixes for fal.
 */
export function buildInpaintRequest(
  state: InpaintFormState,
  sessionId: string | undefined,
  source: SourceImageRef,
  maskDataUrl: string | null,
): InpaintRequest {
  const prompt = state.prompt.trim();
  if (!prompt) {
    throw new Error('Prompt is required for inpaint.');
  }
  if (!maskDataUrl) {
    throw new Error('Paint a mask on the canvas before applying inpaint.');
  }
  if (!source.sourceImageId && !source.sourceImageUrl && !source.sourceImageBase64) {
    throw new Error('A source image is required for inpaint.');
  }
  return {
    sessionId,
    model: trimToUndefined(state.model),
    prompt,
    strength: state.strength,
    maskBase64: stripDataUrlPrefix(maskDataUrl),
    ...source,
  };
}

/**
 * Build a `RemoveBackgroundRequest`. Source image required; no prompt.
 */
export function buildRemoveBackgroundRequest(
  state: RemoveBackgroundFormState,
  sessionId: string | undefined,
  source: SourceImageRef,
): RemoveBackgroundRequest {
  if (!hasSourceImage(source)) {
    throw new Error('A source image is required to remove background.');
  }
  return {
    sessionId,
    model: trimToUndefined(state.model),
    outputFormat: state.outputFormat,
    refineMask: state.refineMask,
    ...source,
  };
}

/**
 * Build an `ImageToImageRequest`. Requires both a prompt and a source.
 * Studio prompt fragments append the same way as Generate; aspect-ratio
 * is intentionally ignored — ImageToImage preserves the source's dims.
 */
export function buildImageToImageRequest(
  state: ImageToImageFormState,
  sessionId: string | undefined,
  source: SourceImageRef,
  studioParams?: StudioParams,
): ImageToImageRequest {
  const fragments = studioParams?.promptFragments ?? [];
  const prompt = mergePromptWithFragments(state.prompt, fragments);
  if (!prompt) {
    throw new Error('Prompt is required to transform.');
  }
  if (!hasSourceImage(source)) {
    throw new Error('A source image is required to transform.');
  }
  return {
    sessionId,
    model: trimToUndefined(state.model),
    prompt,
    strength: state.strength,
    preserveComposition: state.preserveComposition,
    ...source,
  };
}

/**
 * Build an `UpscaleRequest`. Source image required.
 */
export function buildUpscaleRequest(
  state: UpscaleFormState,
  sessionId: string | undefined,
  source: SourceImageRef,
): UpscaleRequest {
  if (!hasSourceImage(source)) {
    throw new Error('A source image is required to upscale.');
  }
  return {
    sessionId,
    model: trimToUndefined(state.model),
    scaleFactor: state.scaleFactor,
    enhanceDetails: state.enhanceDetails,
    denoiseStrength: state.denoiseStrength,
    ...source,
  };
}

/**
 * Build an `EnhanceRequest`. Source image required. An empty enhancements
 * array is encoded as `undefined` so the BE falls back to its default
 * recipe rather than treating the empty list as "apply nothing".
 */
export function buildEnhanceRequest(
  state: EnhanceFormState,
  sessionId: string | undefined,
  source: SourceImageRef,
): EnhanceRequest {
  if (!hasSourceImage(source)) {
    throw new Error('A source image is required to enhance.');
  }
  return {
    sessionId,
    model: trimToUndefined(state.model),
    strength: state.strength,
    enhancements: state.enhancements.length === 0 ? undefined : [...state.enhancements],
    ...source,
  };
}

/**
 * Build a `VariationsRequest`. Source image required.
 */
export function buildVariationsRequest(
  state: VariationsFormState,
  sessionId: string | undefined,
  source: SourceImageRef,
): VariationsRequest {
  if (!hasSourceImage(source)) {
    throw new Error('A source image is required to generate variations.');
  }
  return {
    sessionId,
    model: trimToUndefined(state.model),
    numVariations: state.numVariations,
    variationStrength: state.variationStrength,
    preserveStyle: state.preserveStyle,
    ...source,
  };
}

/**
 * Build a `RemoveObjectRequest`. Requires a mask (canvas-drawn, supplied as
 * a data URL by `KonvaCanvas.onMaskChange`) and a source image. No prompt —
 * the BE fills the masked region with the surrounding scene. Reuses the
 * F12a mask path; mirrors `buildInpaintRequest` shape minus the prompt.
 */
export function buildRemoveObjectRequest(
  state: RemoveObjectFormState,
  sessionId: string | undefined,
  source: SourceImageRef,
  maskDataUrl: string | null,
): RemoveObjectRequest {
  if (!maskDataUrl) {
    throw new Error('Paint a mask on the canvas to mark the object to remove.');
  }
  if (!hasSourceImage(source)) {
    throw new Error('A source image is required to remove an object.');
  }
  return {
    sessionId,
    model: trimToUndefined(state.model),
    maskBase64: stripDataUrlPrefix(maskDataUrl),
    ...source,
  };
}

/**
 * Build an `OutpaintRequest`. Requires a source image; at least one expand
 * direction must be non-zero (otherwise outpaint is a no-op).
 */
export function buildOutpaintRequest(
  state: OutpaintFormState,
  sessionId: string | undefined,
  source: SourceImageRef,
): OutpaintRequest {
  if (!hasSourceImage(source)) {
    throw new Error('A source image is required to outpaint.');
  }
  const direction: ExpandDirection = {
    top: Math.max(0, Math.trunc(state.top)),
    right: Math.max(0, Math.trunc(state.right)),
    bottom: Math.max(0, Math.trunc(state.bottom)),
    left: Math.max(0, Math.trunc(state.left)),
  };
  if (direction.top + direction.right + direction.bottom + direction.left <= 0) {
    throw new Error('Choose at least one direction to expand.');
  }
  return {
    sessionId,
    model: trimToUndefined(state.model),
    prompt: trimToUndefined(state.prompt),
    expandDirection: direction,
    ...source,
  };
}

/**
 * Build a `DenoiseRequest`. Source image required.
 */
export function buildDenoiseRequest(
  state: DenoiseFormState,
  sessionId: string | undefined,
  source: SourceImageRef,
): DenoiseRequest {
  if (!hasSourceImage(source)) {
    throw new Error('A source image is required to denoise.');
  }
  return {
    sessionId,
    model: trimToUndefined(state.model),
    strength: state.strength,
    preserveDetails: state.preserveDetails,
    faceAware: state.faceAware,
    ...source,
  };
}

/**
 * Build a `DeblurRequest`. Source image required.
 */
export function buildDeblurRequest(
  state: DeblurFormState,
  sessionId: string | undefined,
  source: SourceImageRef,
): DeblurRequest {
  if (!hasSourceImage(source)) {
    throw new Error('A source image is required to deblur.');
  }
  return {
    sessionId,
    model: trimToUndefined(state.model),
    blurType: state.blurType,
    strength: state.strength,
    preserveDetails: state.preserveDetails,
    ...source,
  };
}

/**
 * Build a `FaceEnhanceRequest`. Source image required.
 */
export function buildFaceEnhanceRequest(
  state: FaceEnhanceFormState,
  sessionId: string | undefined,
  source: SourceImageRef,
): FaceEnhanceRequest {
  if (!hasSourceImage(source)) {
    throw new Error('A source image is required to enhance faces.');
  }
  return {
    sessionId,
    model: trimToUndefined(state.model),
    enhancementLevel: state.enhancementLevel,
    preserveIdentity: state.preserveIdentity,
    ...source,
  };
}

/**
 * Build a `ColorCorrectRequest`. Source image required. Bundles the per-
 * field form state into the BE's nested `ColorAdjustments` shape. A
 * default-zero adjustments object (every slider at 0, whiteBalance=Auto)
 * encodes as `undefined` so the BE can take its auto-correction path
 * rather than treating "all zeros" as an explicit "no adjustment".
 */
export function buildColorCorrectRequest(
  state: ColorCorrectFormState,
  sessionId: string | undefined,
  source: SourceImageRef,
): ColorCorrectRequest {
  if (!hasSourceImage(source)) {
    throw new Error('A source image is required to color-correct.');
  }
  const hasNonAutoBalance = state.whiteBalance !== 'Auto';
  const hasNonZeroSlider =
    state.exposure !== 0 ||
    state.contrast !== 0 ||
    state.saturation !== 0 ||
    state.temperature !== 0 ||
    state.tint !== 0 ||
    state.highlights !== 0 ||
    state.shadows !== 0 ||
    state.vibrance !== 0;
  const adjustments: ColorAdjustments | undefined =
    hasNonAutoBalance || hasNonZeroSlider
      ? {
          whiteBalance: state.whiteBalance,
          exposure: state.exposure,
          contrast: state.contrast,
          saturation: state.saturation,
          temperature: state.temperature,
          tint: state.tint,
          highlights: state.highlights,
          shadows: state.shadows,
          vibrance: state.vibrance,
        }
      : undefined;
  return {
    sessionId,
    model: trimToUndefined(state.model),
    adjustments,
    ...source,
  };
}

/**
 * Build a `ColorizeRequest`. Source image required. `colorStyle` is free
 * text describing the desired palette (e.g., "1970s warm Kodachrome").
 * Empty string is encoded as undefined so the BE picks defaults.
 *
 * Routed through `/execute` not a typed endpoint (see operationService.ts
 * comment) — that translation happens in the service layer, not here.
 */
export function buildColorizeRequest(
  state: ColorizeFormState,
  sessionId: string | undefined,
  source: SourceImageRef,
): ColorizeRequest {
  if (!hasSourceImage(source)) {
    throw new Error('A source image is required to colorize.');
  }
  return {
    sessionId,
    model: trimToUndefined(state.model),
    colorStyle: trimToUndefined(state.colorStyle),
    enhanceFaces: state.enhanceFaces,
    saturation: state.saturation,
    ...source,
  };
}

/**
 * Build a `MultiAngleRequest`. Source image required.
 */
export function buildMultiAngleRequest(
  state: MultiAngleFormState,
  sessionId: string | undefined,
  source: SourceImageRef,
): MultiAngleRequest {
  if (!hasSourceImage(source)) {
    throw new Error('A source image is required to render multi-angle views.');
  }
  return {
    sessionId,
    model: trimToUndefined(state.model),
    numAngles: state.numAngles,
    angleStepDegrees: state.angleStepDegrees,
    preserveIdentity: state.preserveIdentity,
    ...source,
  };
}

/**
 * Build a `StyleTransferRequest`. Requires a main source (the content
 * image) and a style reference. The style reference is namespaced as
 * `styleReferenceImage{Id|Url|Base64}` on the DTO; we translate from the
 * SourceImageRef shape via `refToNamespacedFields`.
 */
export function buildStyleTransferRequest(
  state: StyleTransferFormState,
  sessionId: string | undefined,
  source: SourceImageRef,
  styleRef: SourceImageRef | null,
): StyleTransferRequest {
  if (!hasSourceImage(source)) {
    throw new Error('A source image is required to apply a style.');
  }
  if (!styleRef || !hasSourceImage(styleRef)) {
    throw new Error('A style reference image is required.');
  }
  return {
    sessionId,
    model: trimToUndefined(state.model),
    styleStrength: state.styleStrength,
    preserveContent: state.preserveContent,
    ...source,
    ...refToNamespacedFields(styleRef, 'styleReferenceImage'),
  };
}

/**
 * Build a `FaceSwapRequest`. Requires a main source (the image receiving
 * the face) and a target reference (the image whose face is the donor).
 * Face indices default to 0 — first detected face in each image.
 */
export function buildFaceSwapRequest(
  state: FaceSwapFormState,
  sessionId: string | undefined,
  source: SourceImageRef,
  targetRef: SourceImageRef | null,
): FaceSwapRequest {
  if (!hasSourceImage(source)) {
    throw new Error('A source image is required to swap a face into.');
  }
  if (!targetRef || !hasSourceImage(targetRef)) {
    throw new Error('A face reference image is required.');
  }
  return {
    sessionId,
    model: trimToUndefined(state.model),
    sourceFaceIndex: state.sourceFaceIndex,
    targetFaceIndex: state.targetFaceIndex,
    enhanceFace: state.enhanceFace,
    ...source,
    ...refToNamespacedFields(targetRef, 'targetImage'),
  };
}

/**
 * Build a `ColorGradeRequest`. Source image required. `gradingStyle` is a
 * required free-text descriptor (e.g., "warm cinematic teal & orange").
 * A reference image is optional — when provided the BE samples its
 * palette for the grade.
 */
export function buildColorGradeRequest(
  state: ColorGradeFormState,
  sessionId: string | undefined,
  source: SourceImageRef,
  referenceRef: SourceImageRef | null,
): ColorGradeRequest {
  if (!hasSourceImage(source)) {
    throw new Error('A source image is required to color-grade.');
  }
  const style = state.gradingStyle.trim();
  if (!style) {
    throw new Error('Describe the grading style.');
  }
  const refFields = referenceRef && hasSourceImage(referenceRef)
    ? refToNamespacedFields(referenceRef, 'referenceImage')
    : {};
  return {
    sessionId,
    model: trimToUndefined(state.model),
    gradingStyle: style,
    strength: state.strength,
    ...source,
    ...refFields,
  };
}

/**
 * Build a `TryOnRequest`. Special-cased: TryOnRequest does NOT extend
 * SourceImageRef. The canvas's active image becomes the model image,
 * and the panel's ReferenceImageInput supplies the garment image.
 * Both are required.
 */
export function buildTryOnRequest(
  state: TryOnFormState,
  sessionId: string | undefined,
  modelRef: SourceImageRef,
  garmentRef: SourceImageRef | null,
): TryOnRequest {
  if (!hasSourceImage(modelRef)) {
    throw new Error('A model image (the active canvas image) is required to try on.');
  }
  if (!garmentRef || !hasSourceImage(garmentRef)) {
    throw new Error('A garment image is required to try on.');
  }
  return {
    sessionId,
    model: trimToUndefined(state.model),
    garmentCategory: trimToUndefined(state.garmentCategory),
    additionalPrompt: trimToUndefined(state.additionalPrompt),
    ...refToNamespacedFields(modelRef, 'modelImage'),
    ...refToNamespacedFields(garmentRef, 'garmentImage'),
  };
}

/**
 * Build a `SketchToImageRequest`. The sketch IS the source image — the
 * canvas's sketch mode (F11c-1) produces a dataURL of dark strokes on
 * white background; we ship it as `sourceImageBase64`. Requires both
 * a non-empty prompt and a non-empty sketch.
 */
export function buildSketchToImageRequest(
  state: SketchToImageFormState,
  sessionId: string | undefined,
  sketchDataUrl: string | null,
): SketchToImageRequest {
  const prompt = state.prompt.trim();
  if (!prompt) {
    throw new Error('Prompt is required to render the sketch.');
  }
  if (!sketchDataUrl) {
    throw new Error('Draw a sketch on the canvas before submitting.');
  }
  return {
    sessionId,
    model: trimToUndefined(state.model),
    prompt,
    sketchStrength: state.sketchStrength,
    width: state.width,
    height: state.height,
    sourceImageBase64: stripDataUrlPrefix(sketchDataUrl),
  };
}

/**
 * Build a `ZoomRequest`. Source image required. Selection rectangle is
 * required — its image-space coords are converted to the BE's
 * centerX/centerY/zoomFactor shape via `rectToZoomParams`.
 *
 * `imageSize` is the source image's pixel dimensions (the canvas loads
 * the active image and knows its dims; ImageLab.tsx threads them in).
 * Without it, we'd have no anchor to convert the rect into a fraction.
 */
export function buildZoomRequest(
  state: ZoomFormState,
  sessionId: string | undefined,
  source: SourceImageRef,
  selection: ImageRect | null,
  imageSize: Size | null,
): ZoomRequest {
  if (!hasSourceImage(source)) {
    throw new Error('A source image is required to zoom.');
  }
  if (!selection) {
    throw new Error('Drag a rectangle on the canvas to choose the zoom region.');
  }
  if (!imageSize) {
    // No image dims yet — the canvas hasn't loaded the active image, so
    // the selection's image-space coords can't be normalized. Defensive;
    // shouldn't happen in normal use because the rect is captured against
    // a loaded image.
    throw new Error('The source image is still loading; try again in a moment.');
  }
  const params = rectToZoomParams(selection, imageSize);
  if (!params) {
    throw new Error('The selected region is invalid; try again.');
  }
  return {
    sessionId,
    model: trimToUndefined(state.model),
    zoomFactor: state.zoomFactor > 0 ? state.zoomFactor : params.zoomFactor,
    centerX: params.centerX,
    centerY: params.centerY,
    detailPrompt: trimToUndefined(state.detailPrompt),
    ...source,
  };
}

/**
 * Build a `ConsistencySheetRequest`. Source image required.
 */
export function buildConsistencySheetRequest(
  state: ConsistencySheetFormState,
  sessionId: string | undefined,
  source: SourceImageRef,
): ConsistencySheetRequest {
  if (!hasSourceImage(source)) {
    throw new Error('A source image of the subject is required.');
  }
  return {
    sessionId,
    model: trimToUndefined(state.model),
    sheetCellCount: state.sheetCellCount,
    variationKind: state.variationKind,
    additionalPrompt: trimToUndefined(state.additionalPrompt),
    ...source,
  };
}
